/**
 * Nombre visible del cliente (API puede usar camelCase o PascalCase).
 */
export function resolverNombreCliente(c: unknown): string {
  if (!c || typeof c !== 'object') return '';
  const o = c as Record<string, unknown>;
  const keys = [
    'razonSocial',
    'RazonSocial',
    'nombre',
    'Nombre',
    'nombreFantasia',
    'NombreFantasia',
    'contacto',
    'Contacto',
  ];
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** Etiquetas completas de tipo IVA (códigos de maestro y de emisión). */
const TIPO_IVA_LABELS_COMPLETOS: Record<string, string> = {
  I: 'Responsable Inscripto',
  RI: 'Responsable Inscripto',
  C: 'Consumidor Final',
  CF: 'Consumidor Final',
  M: 'Monotributo',
  MO: 'Monotributo',
  E: 'Exento',
  EX: 'Exento',
  N: 'No Categorizado',
  NI: 'No Categorizado',
};

export function labelTipoIvaCliente(tipo?: string | null): string {
  const k = String(tipo ?? '').trim().toUpperCase();
  if (!k) return '';
  return TIPO_IVA_LABELS_COMPLETOS[k] ?? String(tipo).trim();
}

/** Domicilio de facturación: calle del cliente + localidad (no envío ni resumen). */
export function formatDomicilioFacturacionCliente(c: unknown): string {
  if (!c || typeof c !== 'object') return '';
  const o = c as Record<string, unknown>;
  const direccion = String(o.direccion ?? o.Direccion ?? '').trim();
  const ciudadRaw = o.ciudad ?? o.Ciudad;
  let nombreCiudad = '';
  let codigoPostal = '';
  if (ciudadRaw && typeof ciudadRaw === 'object') {
    const ci = ciudadRaw as Record<string, unknown>;
    nombreCiudad = String(ci.nombreCiudad ?? ci.NombreCiudad ?? '').trim();
    codigoPostal = String(ci.codigoPostal ?? ci.CodigoPostal ?? '').trim();
  }
  if (!direccion && !nombreCiudad) return '';
  let out = direccion;
  if (nombreCiudad) {
    out = out ? `${out}, ${nombreCiudad}` : nombreCiudad;
  }
  if (codigoPostal) {
    out = out ? `${out} (${codigoPostal})` : `(${codigoPostal})`;
  }
  return out;
}

export type ClienteChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';

export const clienteChipSx = { height: 18, fontSize: '0.65rem', flexShrink: 0, fontWeight: 600 } as const;

const normCodigoTipoIva = (tipo?: string | null): string => {
  const k = String(tipo ?? '').trim().toUpperCase();
  if (k === 'RI' || k === 'I') return 'I';
  if (k === 'CF' || k === 'C') return 'C';
  if (k === 'MO' || k === 'M') return 'M';
  if (k === 'EX' || k === 'E') return 'E';
  if (k === 'NI' || k === 'N') return 'N';
  return k;
};

export function chipColorTipoIvaCliente(tipo?: string | null): ClienteChipColor {
  switch (normCodigoTipoIva(tipo)) {
    case 'I':
      return 'primary';
    case 'M':
      return 'warning';
    case 'E':
      return 'success';
    case 'C':
    case 'N':
      return 'secondary';
    default:
      return 'default';
  }
}

export function chipColorCondVtaCliente(cond: string): ClienteChipColor {
  const n = cond.replace(/\s+/g, '.').toUpperCase();
  if (n === 'CONTADO' || n === 'EFECTIVO') return 'success';
  if (n.includes('CTA')) return 'info';
  if (n === 'CONTRAREEMBOLSO') return 'warning';
  return 'secondary';
}

export function readTipoIvaCliente(c: unknown): string {
  if (!c || typeof c !== 'object') return '';
  const o = c as Record<string, unknown>;
  return String(o.tipoIva ?? o.TipoIva ?? '').trim();
}
